import { useEffect, useRef } from 'react'
import { useMatchScreen } from '../../context/MatchScreenContext'
import { trackEvent } from '../../utils/tracking/mixpanel'
import { AdvisorData, GeneralAdvisorData, RoboAdvisorData } from '../../utils/types'
import { isRoboAdvisorData } from '../modules/AdvisorCardsModule/advisorTypeGuards'
import { useSelfSchedulingEligibility } from './useSelfSchedulingEligibility'

export const SELF_SCHEDULE_ADVISOR_MATCHED_EVENT = 'Self Schedule Advisor Matched' as const

/**
 * Fire a single "Self Schedule Advisor Matched" Mixpanel event on funnel
 * load, once the self-schedule CTAs are presented. Gated on the same
 * `useSelfSchedulingEligibility` check that renders each card's CTA, so the event
 * only fires when the CTAs actually appear. The `matched advisors` property lists
 * every presented advisor's name, giving analytics a denominator for CTA presentations.
 *
 * Only non-robo advisors present a self-schedule button (the CTA lives in
 * `NonRoboAdvisorContent`, not `RoboAdvisorContent`), so robo advisors are excluded
 * from the list. If no advisor presents a CTA, the event does not fire.
 *
 * Takes the already-resolved advisor list so the tracked names match the rendered
 * cards exactly (same randomized order/set).
 */
export const useTrackSelfScheduleMatched = (advisorsData: GeneralAdvisorData | undefined): void => {
  const isSelfSchedulingEligible = useSelfSchedulingEligibility()
  const { state: { pageKey } } = useMatchScreen()
  const hasTracked = useRef(false)

  useEffect(() => {
    if (hasTracked.current) return
    if (!isSelfSchedulingEligible || !advisorsData || advisorsData.length === 0) return

    const matchedAdvisors = (advisorsData as (AdvisorData | RoboAdvisorData)[])
      .filter((advisor) => !isRoboAdvisorData(advisor))
      .map(({ advisor }) => advisor.name)

    if (matchedAdvisors.length === 0) return

    hasTracked.current = true

    trackEvent('MatchScreen', SELF_SCHEDULE_ADVISOR_MATCHED_EVENT, {
      page_key: pageKey,
      'matched advisors': matchedAdvisors
    })
  }, [isSelfSchedulingEligible, advisorsData, pageKey])
}
